import { useNotifications } from '@risksmart-app/components/src/notifications/useNotifications';
import type {
  AssessmentResultParentWithObligationResultsResponseRow,
  ObligationRegisterResponse,
  ObligationRegisterResponseRow,
} from '@risksmart-app/trpc/src/types';
import type { GetObligationsQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useTRPC } from 'src/utils/trpc';

import { useIsFeatureVisibleToOrg } from '@/utils/featureFlags';

function mapTrpcAssessmentResultParentToGraphQL(
  assessment_result_parent: AssessmentResultParentWithObligationResultsResponseRow
): GetObligationsQuery['assessment_result_parent'][number] {
  return {
    ...assessment_result_parent,
  };
}

function mapTrpcObligationToGraphQL(
  obligation: ObligationRegisterResponseRow
): GetObligationsQuery['obligation'][number] {
  return {
    ...obligation,
    Parent: obligation.parent,
    BreachedIssues: obligation.issues,
    CreatedByUser: obligation.CreatedByUser,
    ModifiedByUser: obligation.ModifiedByUser,
    controls_aggregate: {
      aggregate: {
        count: obligation.controls.length,
      },
    },
  };
}

/**
 * Maps TRPC obligation data to match the GraphQL query structure
 */
export function mapTrpcObligationsToGraphQL(
  trpcData: ObligationRegisterResponse | undefined
): GetObligationsQuery | undefined {
  if (!trpcData) {
    return undefined;
  }

  return {
    obligation: trpcData.obligation.map((obligation) =>
      mapTrpcObligationToGraphQL(obligation)
    ),
    assessment_result_parent: trpcData.assessment_result_parent
      ? trpcData.assessment_result_parent.map((parent) =>
          mapTrpcAssessmentResultParentToGraphQL(parent)
        )
      : [],
  };
}

export const useGetObligationsRegister = (skip?: boolean) => {
  const trpcEnabled = useIsFeatureVisibleToOrg('trpc');
  const trpc = useTRPC();
  const { addNotification } = useNotifications();
  const {
    data: trpcData,
    isLoading: trpcLoading,
    refetch: trpcRefetch,
    error: trpcError,
  } = useQuery({
    ...trpc.frontend.obligation.register.queryOptions(),

    enabled: trpcEnabled && !skip, // Only enable TRPC query when flag is true
  });

  // Handle TRPC errors
  useEffect(() => {
    if (trpcEnabled && trpcError) {
      addNotification({
        type: 'error',
        content: <>{trpcError.message}</>,
      });
    }
  }, [trpcEnabled, trpcError, addNotification]);

  // Transform TRPC data to match the same structure as GraphQL query
  const data = mapTrpcObligationsToGraphQL(trpcData);

  return {
    loading: trpcLoading,
    data,
    refetch: () => trpcRefetch(),
  };
};
